import { Injectable } from '@angular/core';
import { NavigationStart, Router, RoutesRecognized } from '@angular/router';
import { environment } from '@env/environment';
import { ContextDossier } from '@kv-common-components/kv-common-components';
import {
    AuthenticationService,
    EspaceClientService,
    QuestionnaireSanteService,
    QuestionnaireSanteSimplifieService,
    ReponseQuestionnaireSanteSimplifie,
    User,
} from '@kv-common-components/kx-services';
import { NGXLogger } from 'ngx-logger';
import { filter, pairwise, take } from 'rxjs/operators';
import { TagCommanderMapperService } from './tag-commander-mapper.service';
import { AssuranceHelperService } from '@shared/services/assurance-helper.service';
import { RouteEnum } from './route-enum';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class RouterHelperService {
    public static readonly codeProduitIndiv = 'GBA001';

    public static readonly urlSepaMandate = 'souscription/sepa-mandate';
    public static readonly urlFinalisation = 'souscription/finalisation';
    public static readonly urlDocumentMedicaleList =
        'souscription/document-medicale/document-list';
    public static readonly urlParcoursMedicalConsentements =
        'souscription/parcours-medical/consentements';

    public tagCommanderContext: any;
    currentUser: User = this.authenticationService.currentUserValue;
    isFromQSS = false;
    previousUrl = '';
    constructor(
        private router: Router,
        private authenticationService: AuthenticationService,
        private questionnaireSanteService: QuestionnaireSanteService,
        private questionnaireSanteSimplifieService: QuestionnaireSanteSimplifieService,
        private logger: NGXLogger,
        private espaceClientService: EspaceClientService,
        private tagCommanderMapperService: TagCommanderMapperService
    ) {
        router.events
            .pipe(filter((event) => event instanceof NavigationStart))

            .subscribe((event: NavigationStart) => {
                if (event.navigationTrigger === 'popstate') {
                    this.logger.debug(
                        `catched back button, redirecting to ${RouteEnum.dashboard}`
                    );
                    this.router.navigateByUrl(RouteEnum.dashboard);
                } else {
                    this.navigationChange(event);
                }
            });

        this.router.events
            .pipe(
                filter((evt: any) => evt instanceof RoutesRecognized),
                pairwise()
            )
            .subscribe((events: RoutesRecognized[]) => {
                this.previousUrl = events[0].urlAfterRedirects;
            });
    }

    navigateByStepUrl(url: string) {
        this.router.navigateByUrl(url);
    }

    routeAfterSignature(idPersonne: string, idDossier: string) {
        this.espaceClientService
            .getEspaceClient(idPersonne)
            .subscribe((espaceClient) => {
                const contextDossier =
                    ContextDossier.initContextDossierFromEspaceClient(
                        idPersonne,
                        String(idDossier),
                        espaceClient
                    );
                let service = 'questionnaireSanteSimplifieService';
                let responses =
                    contextDossier.emprunteur.assurance
                        .listeReponsesQuestionsSanteSimplifie;
                if (
                    contextDossier.emprunteur.assurance
                        .listeReponsesQuestionsSante.length > 0
                ) {
                    service = 'questionnaireSanteService';
                    responses =
                        contextDossier.emprunteur.assurance
                            .listeReponsesQuestionsSante;
                }
                this[service]
                    .postValidation(idPersonne, idDossier, responses)
                    .subscribe((data) => {
                        this.navigateAfterQSS(data, contextDossier);
                    });
            });
    }

    navigateAfterQSS(postValidation: any, contextDossier?: any) {
        //TODO EADE ADAPTATION
        if (postValidation.codeDecision === 'OK') {
            this.router.navigateByUrl('/souscription/signature');
        } else if (
            postValidation.codeDecision === 'DECISION_AIA_IMMEDIATE' ||
            postValidation.codeDecision === 'DECISION_AIA_DIFFERE' ||
            postValidation.codeDecision === 'DECISION_AIA_DIFFERE_PIJ'
        ) {
            if (
                contextDossier &&
                contextDossier.emprunteur.topSansSelectionMedicale
            ) {
                this.router.navigateByUrl('/souscription/finalisation');
            } else {
                this.router.navigateByUrl(
                    '/souscription/document-medicale/chargement'
                );
            }
        } else if (postValidation.codeDecision === 'BASCUL_QS') {
            this.isFromQSS = true;
            this.router.navigateByUrl(
                '/souscription/parcours-medical/questionnaire-de-sante'
            );
        } else {
            this.router.navigateByUrl('/refus');
        }
    }

    navigateToQSorQSS() {
        this.questionnaireSanteService.currentQuestionnaireSante
            .pipe(take(1))
            .subscribe((questionnaireSante) => {
                if (
                    questionnaireSante.listeQuestionsQuestionnaireSanteSimplifie &&
                    questionnaireSante.listeQuestionsQuestionnaireSanteSimplifie
                        .length
                ) {
                    this.router.navigateByUrl(
                        '/souscription/parcours-medical/questionnaire-de-sante-simplifie'
                    );
                } else {
                    this.router.navigateByUrl(
                        '/souscription/parcours-medical/questionnaire-de-sante'
                    );
                }
            });
    }

    initializeContextDossier(espaceClient: any): ContextDossier {
        const idPersonne =
            this.authenticationService.currentUserValue.idPersonne.toString();
        const idDossier =
            this.authenticationService.currentUserValue.idDossier.toString();

        return ContextDossier.initContextDossierFromEspaceClient(
            idPersonne,
            idDossier,
            espaceClient
        );
    }

    isStatutSignaturePropo(): boolean {
        let isStatutSignaturePropo = false;
        const refreshedEspaceClient =
            this.espaceClientService.refreshedEspaceClient;
        if (refreshedEspaceClient) {
            const contextDossier = this.initializeContextDossier(
                refreshedEspaceClient
            );
            isStatutSignaturePropo =
                AssuranceHelperService.isCodeAiaStatutDAForSignaturePropo(
                    contextDossier.emprunteur.assurance.statutDA
                );
        }

        return isStatutSignaturePropo;
    }

    isStatutFinalisationAfterPropoSignature(): boolean {
        let isStatutFinalisationAfterPropoSignature = false;
        const refreshedEspaceClient =
            this.espaceClientService.refreshedEspaceClient;
        if (refreshedEspaceClient) {
            const contextDossier = this.initializeContextDossier(
                refreshedEspaceClient
            );
            isStatutFinalisationAfterPropoSignature =
                AssuranceHelperService.isCodeAiaStatutDAForFinalisationAfterPropoSignature(
                    contextDossier.emprunteur.assurance.statutDA
                );
        }

        return isStatutFinalisationAfterPropoSignature;
    }

    sendFakeQssLemoine(idPersonne: string, idDossier: string): Observable<any> {
        this.logger.debug(
            'Not Eligible for medical selection: sending fictional QSS'
        );
        return new Observable((observer) => {
            const qssFictif: ReponseQuestionnaireSanteSimplifie[] = [
                {
                    id: '1',
                    numero: 'qst_wb_1.1',
                    idTailleM: 'qst_wb_1.1a',
                    idTailleCm: 'qst_wb_1.1b',
                    idPoids: 'qst_wb_1.1c',
                    tailleM: 1,
                    tailleCm: 70,
                    poids: 70,
                },
                { id: '2', numero: 'qst_wb_2.1', ouiNon: 'non' },
                { id: '3', numero: 'qst_wb_3.1', ouiNon: 'non' },
                { id: '4', numero: 'qst_wb_4.1', ouiNon: 'non' },
                { id: '5', numero: 'qst_wb_5.1', ouiNon: 'non' },
            ];
            this.questionnaireSanteSimplifieService
                .postValidation(idPersonne, idDossier, qssFictif, true)
                .subscribe((postValidation) => {
                    observer.next(postValidation);
                    observer.complete();
                });
        });
    }
    public initTagCommander() {
        const tagCommanderVarScript = this.createScript(
            environment.tagCommanderVarUrl
        );
        tagCommanderVarScript.onload = () => {
            this.logger.info('tagCommanderVarScript loaded');
            const tagCommanderScript = this.createScript(
                environment.tagCommanderUrl
            );
            tagCommanderScript.onload = () => {
                this.logger.info('tagCommanderScript loaded');
            };
            document
                .getElementsByTagName('head')[0]
                .appendChild(tagCommanderScript);
        };
        document
            .getElementsByTagName('head')[0]
            .appendChild(tagCommanderVarScript);
    }

    public createScript(url: string): HTMLScriptElement {
        const node = document.createElement('script');
        node.src = url;
        node.type = 'text/javascript';
        node.async = true;
        node.charset = 'utf-8';

        return node;
    }

    public navigationChange(event) {
        this.logger.info('Navigation changed to : ', event.url);
        const pageName = this.getEadePageName(event.url);

        const currentUser = this.authenticationService.currentUserValue;
        //deconexion case or first connexion
        if (
            pageName === 'login' ||
            pageName === 'otp' ||
            pageName.includes('activation')
        ) {
            this.simpleTagCommanderCall(pageName);
            this.tagCommanderContext = null;
        } else if (
            this.tagCommanderContext &&
            this.tagCommanderContext.projet_dossier_id === currentUser.idDossier
        ) {
            this.simpleTagCommanderCall(pageName);
        } else {
            this.logger.info('Need to update tagCommanderContext');
            const globalData = window.candConfig.tagCommander;
            if (currentUser) {
                this.espaceClientService
                    .getrefreshedEspaceClient(currentUser.idPersonne)
                    .subscribe((espaceClient) => {
                        if (espaceClient.listeProjetsCredits.length > 0) {
                            this.tagCommanderContext = {
                                /* eslint-disable-next-line @typescript-eslint/naming-convention */
                                page_nom: pageName,
                                ...this.tagCommanderMapperService.mapProjet(
                                    currentUser,
                                    espaceClient
                                ),
                                ...this.tagCommanderMapperService.mapGlobalData(
                                    currentUser,
                                    globalData
                                ),
                                ...this.tagCommanderMapperService.mapUtilisateur(
                                    currentUser,
                                    espaceClient.personne
                                ),
                            };
                        } else {
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            this.tagCommanderContext = { page_nom: pageName };
                        }
                        this.logger.info('Setting tc_vars with : ', {
                            tagCommanderContext: this.tagCommanderContext,
                        });
                        window.tc_vars = this.tagCommanderContext;
                        this.initTagCommander();
                    });
            }
        }
    }

    getTopSansSelectionMedicale(): boolean {
        const refreshedEspaceClient =
            this.espaceClientService.refreshedEspaceClient;
        if (!refreshedEspaceClient) {
            this.logger.error(
                'Could not get refreshedEspaceClient from espaceClientService: topSansSelectionMedicale is undefined'
            );
            return undefined;
        }
        const contextDossier: ContextDossier = this.initializeContextDossier(
            refreshedEspaceClient
        );
        return contextDossier.emprunteur.topSansSelectionMedicale;
    }

    private getEadePageName(url: string): string {
        const actualUrl = url.substring(url.lastIndexOf('/') + 1, url.length);
        return (
            this.tagCommanderMapperService.eadeUrlForTagCommander[actualUrl] ||
            actualUrl
        );
    }

    private simpleTagCommanderCall(pageName: string) {
        this.logger.info('tagCommanderContext uptodate : ', {
            tagCommanderContext: this.tagCommanderContext,
        });
        if (!this.tagCommanderContext) {
            this.tagCommanderContext = {};
        }
        /* eslint-disable-next-line @typescript-eslint/naming-convention */
        this.tagCommanderContext.page_nom = pageName;
        window.tc_vars = this.tagCommanderContext;
        this.initTagCommander();
    }
}
